import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  BellPlus,
  Check,
  ChevronLeft,
  FlaskConical,
  BookOpen,
  Stethoscope,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { computedAttributesApi } from '@/api/endpoints/computedAttributes';
import type { ComputedAttribute, ComputedAttributeInput } from '@/api/endpoints/computedAttributes';
import { notificationsApi } from '@/api/endpoints/notifications';
import { permissionsApi } from '@/api/endpoints/permissions';
import { groupsApi } from '@/api/endpoints/groups';
import { usersApi } from '@/api/endpoints/users';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { useEntitlements } from '@/stores/entitlements';
import { ALARM_LABELS, translateAttribute, formatAttributeValue } from '@/lib/traccarLabels';
import { findDeliveryGaps, describeGap } from '@/lib/notificationDelivery';
import type { CheckedChannel } from '@/lib/notificationDelivery';
import { cn } from '@/lib/utils';
import type { Notification, User } from '@/api/types';
import {
  buildExpression,
  describeDraft,
  OPERATOR_OPTIONS,
  parseExpression,
  validateRuleName,
} from './customRuleExpression';
import type { Condition, RuleDraft } from './customRuleExpression';
import { NotificationDiagnosticsModal } from './NotificationDiagnosticsModal';
import { mensagemDeErro } from '@/lib/apiError';

/** A chave que o atributo computado precisa gravar para virar evento de alarme. */
const TARGET_ATTRIBUTE = 'alarm';

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Painel web',
  firebase: 'App (push)',
  mail: 'E-mail',
  telegram: 'Telegram',
  sms: 'SMS',
};
const CHANNEL_ORDER = ['web', 'firebase', 'mail', 'telegram', 'sms'];

const EMPTY_CONDITION: Condition = { attribute: '', operator: 'eq', value: '' };

interface Opt {
  id: number;
  name: string;
}

/** Uma linha da tela: a condição, a regra de envio pareada pelo nome, e a condição relida. */
interface RuleRow {
  attribute: ComputedAttribute;
  notification: Notification | null;
  /** `null` = expressão fora do formato desta tela (escrita à mão no Traccar). */
  draft: RuleDraft | null;
}

const errorText = mensagemDeErro;

export function CustomAlarmsPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  // O `AttributeResource` do Traccar exige administrador nos quatro verbos de escrita, e é
  // por isso que esta tela já foi só de admin. Desde 2026-08-10 a escrita passa pelo nosso
  // backend (`/geo/v1/custom-alarms`), que assina com a sessão administrativa depois de
  // conferir escopo e expressão — então a revenda entra aqui pela porta da frente.
  const podeUsar = papel === 'admin' || papel === 'revenda';

  const attributes = useQuery({
    queryKey: ['computed-attributes'],
    queryFn: () => computedAttributesApi.list(),
    enabled: podeUsar,
  });
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    enabled: podeUsar,
  });
  const notificators = useQuery({
    queryKey: ['notification-notificators'],
    queryFn: () => notificationsApi.notificators(),
    enabled: podeUsar,
    retry: false,
  });
  const groups = useQuery({ queryKey: ['groups'], queryFn: () => groupsApi.list(), enabled: podeUsar });
  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(),
    enabled: podeUsar,
    retry: false,
  });
  const fleet = useFleetStore((s) => s.devices);

  const [editing, setEditing] = useState<RuleRow | 'new' | null>(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [search, setSearch] = useState('');

  const serverChannels = useMemo(() => {
    const list = notificators.data;
    if (!list || list.length === 0) return CHANNEL_ORDER;
    return CHANNEL_ORDER.filter((c) => list.some((n) => n.type === c));
  }, [notificators.data]);

  const deviceOpts = useMemo(
    () => Array.from(fleet.values()).map((d) => ({ id: d.id, name: d.name })),
    [fleet],
  );
  const groupOpts = useMemo(() => (groups.data ?? []).map((g) => ({ id: g.id, name: g.name })), [groups.data]);
  const userOpts = useMemo(
    () => (users.data ?? []).map((u) => ({ id: u.id, name: u.name || u.email || `#${u.id}` })),
    [users.data],
  );

  /**
   * Pareia condição e regra de envio PELO NOME DO ALARME, que é exatamente o que as une em tempo de
   * execução: a condição grava o nome em `alarm` e a regra filtra `attributes.alarms` por igualdade
   * de texto. Se o par existe aqui, existe no servidor.
   */
  const rows: RuleRow[] = useMemo(() => {
    const alarmRules = (notifications.data ?? []).filter((n) => n.type === 'alarm');
    return (attributes.data ?? [])
      .filter((a) => a.attribute === TARGET_ATTRIBUTE)
      .map((attribute) => {
        const draft = parseExpression(attribute.expression);
        const name = (draft?.name ?? attribute.description ?? '').trim();
        const notification = name
          ? (alarmRules.find((n) => String(n.attributes?.alarms ?? '').trim() === name) ?? null)
          : null;
        return { attribute, notification, draft };
      })
      .sort((a, b) => b.attribute.id - a.attribute.id);
  }, [attributes.data, notifications.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.draft?.name || r.attribute.description || '').toLowerCase();
      return name.includes(q) || r.attribute.expression.toLowerCase().includes(q);
    });
  }, [rows, search]);

  if (!podeUsar) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Link
          to="/notifications"
          className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
        >
          <ChevronLeft className="h-5 w-5" />
          Voltar para notificações
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Lock className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-bold">Acesso restrito a administradores e revendas</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Criar a condição de um alarme personalizado altera como o servidor avalia cada posição
              recebida, então fica com quem administra a frota. As regras de envio comuns continuam
              disponíveis em Notificações.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loading = attributes.isLoading || notifications.isLoading;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/notifications"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar para notificações
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Alarmes personalizados</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Transforma qualquer informação que o rastreador envia (uma entrada digital, um sensor, uma leitura
            do motor) em um alarme com nome próprio, que passa a existir como qualquer outro: entra no
            histórico, aparece em relatório e dispara aviso.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/manuais/alarmes-personalizados"
            className="inline-flex h-10 items-center gap-1.5 rounded-md border border-input px-4 text-sm font-medium hover:bg-accent"
          >
            <BookOpen className="h-4 w-4" />
            Manual
          </Link>
          <Button type="button" variant="outline" onClick={() => setDiagOpen(true)} className="gap-1.5">
            <Stethoscope className="h-4 w-4" />
            Diagnóstico
          </Button>
          <Button onClick={() => setEditing('new')} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Novo alarme
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou pela expressão"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {attributes.error ? (
        <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-sm font-medium text-status-blocked">
          {errorText(attributes.error)}
        </p>
      ) : loading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando alarmes personalizados…</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <BellPlus className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search ? 'Nenhum alarme corresponde à busca.' : 'Nenhum alarme personalizado cadastrado.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((row) => (
            <RuleCard key={row.attribute.id} row={row} onEdit={() => setEditing(row)} />
          ))}
        </div>
      )}

      {diagOpen && <NotificationDiagnosticsModal onClose={() => setDiagOpen(false)} />}

      {editing && (
        <RuleEditor
          initial={editing === 'new' ? null : editing}
          serverChannels={serverChannels}
          devices={deviceOpts}
          groups={groupOpts}
          users={userOpts}
          allUsers={users.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RuleCard({ row, onEdit }: { row: RuleRow; onEdit: () => void }) {
  const name = (row.draft?.name || row.attribute.description || `#${row.attribute.id}`).trim();
  const channels = String(row.notification?.notificators ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold">{name}</p>
            {!row.draft && (
              <span className="mt-1 inline-block rounded-full bg-status-ready/10 px-2 py-0.5 text-[10px] font-semibold text-status-ready">
                expressão manual
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-muted-foreground">Quando</p>
          {row.draft ? (
            <p className="text-xs">{describeDraft(row.draft)}</p>
          ) : (
            <p className="max-h-16 overflow-auto break-all rounded bg-secondary/60 p-1.5 font-mono text-[10px]">
              {row.attribute.expression}
            </p>
          )}
        </div>

        <div>
          <p className="text-[11px] font-semibold text-muted-foreground">Envio</p>
          {row.notification ? (
            <div className="flex flex-wrap gap-1">
              {channels.map((c) => (
                <span key={c} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  {CHANNEL_LABEL[c] ?? c}
                </span>
              ))}
            </div>
          ) : (
            <p
              className="text-[11px] text-status-ready"
              title="Nenhuma regra desta lista filtra este nome. Ainda pode ser entregue por uma regra de alarme sem filtro de subtipo vinculada ao mesmo veículo."
            >
              sem regra própria de envio
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface FormState {
  name: string;
  logic: 'and' | 'or';
  conditions: Condition[];
  advanced: boolean;
  rawExpression: string;
  channels: string[];
  deviceIds: number[];
  groupIds: number[];
  userIds: number[];
}

function RuleEditor({
  initial,
  serverChannels,
  devices,
  groups,
  users,
  allUsers,
  onClose,
}: {
  initial: RuleRow | null;
  serverChannels: string[];
  devices: Opt[];
  groups: Opt[];
  users: Opt[];
  allUsers: User[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(() => {
    if (!initial) {
      return {
        name: '',
        logic: 'and',
        conditions: [{ ...EMPTY_CONDITION }],
        advanced: false,
        rawExpression: '',
        channels: serverChannels.includes('firebase') ? ['web', 'firebase'] : ['web'],
        deviceIds: [],
        groupIds: [],
        userIds: [],
      };
    }
    const channels = String(initial.notification?.notificators ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      name: initial.draft?.name || initial.attribute.description || '',
      logic: initial.draft?.logic ?? 'and',
      conditions: initial.draft?.conditions?.length ? initial.draft.conditions : [{ ...EMPTY_CONDITION }],
      // Expressão fora do formato desta tela abre no modo avançado com o texto cru: melhor não
      // editar do que exibir uma condição que a tela inventou.
      advanced: !initial.draft,
      rawExpression: initial.attribute.expression,
      channels: channels.length ? channels : ['web'],
      deviceIds: [],
      groupIds: [],
      userIds: [],
    };
  });
  const [touched, setTouched] = useState(false);
  const [refDeviceId, setRefDeviceId] = useState<number | ''>('');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const nameError = form.name.trim() ? validateRuleName(form.name) : null;

  const conditionsComplete =
    form.conditions.length > 0 &&
    form.conditions.every(
      (c) =>
        c.attribute.trim() &&
        String(c.value ?? '').trim() &&
        (c.operator !== 'between' || String(c.value2 ?? '').trim()),
    );

  const expression = form.advanced
    ? form.rawExpression.trim()
    : conditionsComplete && form.name.trim()
      ? buildExpression({ name: form.name.trim(), logic: form.logic, conditions: form.conditions })
      : '';

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    const nErr = validateRuleName(form.name);
    if (nErr) e.name = nErr;
    if (!expression) e.condition = form.advanced ? 'Escreva a expressão.' : 'Monte ao menos uma condição completa.';
    if (form.channels.length === 0) e.channels = 'Escolha pelo menos 1 canal.';
    if (!initial && form.deviceIds.length === 0 && form.groupIds.length === 0)
      e.scope = 'Selecione ≥1 veículo ou grupo: a condição só é avaliada nos veículos vinculados.';
    return e;
  }, [form, expression, initial]);
  const valid = Object.keys(errors).length === 0;

  // Avisos: não impedem gravar, alertam sobre falha silenciosa depois.
  const deliveryGaps = useMemo(() => {
    const channels: CheckedChannel[] = [];
    if (form.channels.includes('firebase')) channels.push('firebase');
    if (form.channels.includes('telegram')) channels.push('telegram');
    return findDeliveryGaps(allUsers, form.userIds, channels);
  }, [allUsers, form.userIds, form.channels]);

  const collidesWithNative = !!form.name.trim() && Object.prototype.hasOwnProperty.call(ALARM_LABELS, form.name.trim());

  const payload = (): ComputedAttributeInput => ({
    description: form.name.trim(),
    attribute: TARGET_ATTRIBUTE,
    type: 'string',
    expression,
    ...(initial?.attribute.priority !== undefined ? { priority: initial.attribute.priority } : {}),
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const deviceId = refDeviceId || form.deviceIds[0];
      if (!deviceId) throw new Error('Escolha um veículo de referência para testar.');
      return computedAttributesApi.test(Number(deviceId), payload());
    },
    onSuccess: (result) => {
      setTestResult(
        result === null
          ? {
              ok: false,
              text: 'A expressão é válida, mas a condição NÃO está satisfeita na última posição deste veículo. Isso é o esperado quando o gatilho não está ativo agora.',
            }
          : { ok: true, text: `Condição satisfeita agora. O alarme gravado seria "${result}".` },
      );
    },
    onError: (e) => setTestResult({ ok: false, text: errorText(e) }),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const name = form.name.trim();

      // 1) condição: é ela que injeta o alarme na posição
      const saved = initial
        ? await computedAttributesApi.update(initial.attribute.id, payload())
        : await computedAttributesApi.create(payload());

      // 2) alcance da condição. Sem vínculo, o servidor nunca avalia a expressão.
      // Uma chamada só, e no backend: vincular ao atributo computado exige acesso de escrita
      // a ele, que é de admin, e o backend reconcilia sobrando e faltando de uma vez.
      const links: Promise<unknown>[] = [];
      await computedAttributesApi.setAlcance(saved.id, {
        deviceIds: form.deviceIds,
        groupIds: form.groupIds,
      });

      // 3) regra de envio que filtra exatamente esse nome
      const attributes: Record<string, unknown> = { ...(initial?.notification?.attributes ?? {}) };
      attributes.name = name;
      attributes.alarms = name;
      if (form.channels.includes('firebase')) attributes.firebase = true;
      else delete attributes.firebase;
      const notificators = form.channels.filter((c) => serverChannels.includes(c)).join(',') || 'web';
      const body = { type: 'alarm', always: false, notificators, attributes };

      let notificationId: number;
      if (initial?.notification) {
        notificationId = initial.notification.id;
        await notificationsApi.update(notificationId, { ...initial.notification, ...body } as Notification);
      } else {
        const created = await notificationsApi.create(body);
        notificationId = created.id;
      }

      // 4) alcance e destinatários do envio
      for (const deviceId of form.deviceIds) links.push(permissionsApi.link({ deviceId, notificationId }));
      for (const groupId of form.groupIds) links.push(permissionsApi.link({ groupId, notificationId }));
      for (const userId of form.userIds) links.push(permissionsApi.link({ userId, notificationId }));
      // Vínculo repetido responde 400; `allSettled` para um vínculo já existente não derrubar o resto.
      await Promise.allSettled(links);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['computed-attributes'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await computedAttributesApi.delete(initial!.attribute.id);
      if (initial!.notification) {
        await notificationsApi.delete(initial!.notification.id).catch(() => {
          /* condição já removida; regra de envio órfã é visível na tela de Notificações */
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['computed-attributes'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const setCondition = (i: number, patch: Partial<Condition>) =>
    set(
      'conditions',
      form.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">{initial ? 'Editar alarme personalizado' : 'Novo alarme personalizado'}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nome do alarme" required error={touched ? errors.name : nameError ?? undefined}>
            <Input
              value={form.name}
              maxLength={100}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Ex.: PORÃO, TEMPERATURA ALTA"
            />
          </Field>
          <p className="rounded-md bg-secondary/60 px-3 py-2 text-[11px] text-muted-foreground">
            O nome não é um rótulo: é ele que liga as duas metades da regra. A condição grava esse texto no
            veículo e o envio procura exatamente esse texto. Se renomear, renomeie por aqui, que ajusta os dois
            lados de uma vez.
          </p>
          {collidesWithNative && (
            <Warn>
              Já existe um alarme de fábrica com esse código. Regras antigas que filtram esse nome passariam a
              disparar junto com a sua. Prefira um nome próprio.
            </Warn>
          )}

          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">Condição</p>
              <button
                type="button"
                onClick={() => set('advanced', !form.advanced)}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                {form.advanced ? 'Voltar ao construtor' : 'Escrever expressão à mão'}
              </button>
            </div>

            {form.advanced ? (
              <textarea
                value={form.rawExpression}
                onChange={(e) => set('rawExpression', e.target.value)}
                rows={3}
                spellCheck={false}
                placeholder="input == 101 ? 'PORAO' : null"
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              />
            ) : (
              <>
                {form.conditions.map((c, i) => (
                  <div key={i} className="space-y-1 rounded-md bg-secondary/40 p-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <input
                        value={c.attribute}
                        onChange={(e) => setCondition(i, { attribute: e.target.value })}
                        placeholder="atributo (ex.: input)"
                        list="nautmax-attr-sugestoes"
                        className="h-9 min-w-[9rem] flex-1 rounded-md border border-input bg-background px-2 text-sm"
                      />
                      <select
                        value={c.operator}
                        onChange={(e) => setCondition(i, { operator: e.target.value as Condition['operator'] })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      >
                        {OPERATOR_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <input
                        value={c.value}
                        onChange={(e) => setCondition(i, { value: e.target.value })}
                        placeholder={
                          c.operator === 'in' ? '100, 101' : c.operator === 'digitOn' ? 'casa' : 'valor'
                        }
                        className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
                      />
                      {c.operator === 'between' && (
                        <input
                          value={c.value2 ?? ''}
                          onChange={(e) => setCondition(i, { value2: e.target.value })}
                          placeholder="até"
                          className="h-9 w-20 rounded-md border border-input bg-background px-2 text-sm"
                        />
                      )}
                      {form.conditions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => set('conditions', form.conditions.filter((_, idx) => idx !== i))}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                          aria-label="Remover condição"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {c.operator === 'digitOn' && (
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        Muitos rastreadores mandam todas as entradas num número só. Conte a casa da
                        direita para a esquerda: 1 é a última, 2 é a do meio, e assim por diante. No
                        Suntech, a casa 1 é a ignição e a casa 3 é a entrada 2. Assim a regra continua
                        valendo mesmo com outra entrada acionada junto.
                      </p>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => set('conditions', [...form.conditions, { ...EMPTY_CONDITION }])}
                    className="gap-1"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Condição
                  </Button>
                  {form.conditions.length > 1 && (
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-muted-foreground">Combinar com</span>
                      {(['and', 'or'] as const).map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => set('logic', l)}
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-bold',
                            form.logic === l ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
                          )}
                        >
                          {l === 'and' ? 'E (todas)' : 'OU (qualquer uma)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {touched && errors.condition && <p className="text-[11px] font-semibold text-status-blocked">{errors.condition}</p>}

            <AttributePicker devices={devices} onPickDevice={(id) => setRefDeviceId(id)} />

            {expression && (
              <div>
                <p className="text-[11px] font-semibold text-muted-foreground">Expressão gerada</p>
                <p className="break-all rounded bg-secondary/60 p-2 font-mono text-[10px]">{expression}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={refDeviceId}
                onChange={(e) => setRefDeviceId(e.target.value ? Number(e.target.value) : '')}
                className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">— veículo de referência para o teste —</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!expression || testMutation.isPending}
                onClick={() => { setTestResult(null); testMutation.mutate(); }}
                className="gap-1.5"
              >
                <FlaskConical className="h-3.5 w-3.5" />
                {testMutation.isPending ? 'Testando…' : 'Testar agora'}
              </Button>
            </div>
            {testResult && (
              <p
                className={cn(
                  'rounded-md px-3 py-2 text-[11px] font-medium',
                  testResult.ok ? 'bg-status-running/10 text-status-running' : 'bg-secondary text-muted-foreground',
                )}
              >
                {testResult.text}
              </p>
            )}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">
              Canais {touched && errors.channels && <span className="text-status-blocked">· {errors.channels}</span>}
            </p>
            {serverChannels.map((c) => (
              <Toggle
                key={c}
                label={CHANNEL_LABEL[c] ?? c}
                checked={form.channels.includes(c)}
                onChange={(v) => set('channels', v ? [...form.channels, c] : form.channels.filter((x) => x !== c))}
              />
            ))}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Alcance (onde a condição é avaliada)</p>
            <p className="text-[11px] text-muted-foreground">Veículos</p>
            <MultiSelect options={devices} selected={form.deviceIds} onChange={(ids) => set('deviceIds', ids)} placeholder="Buscar veículo" />
            <p className="text-[11px] text-muted-foreground">Grupos</p>
            <MultiSelect options={groups} selected={form.groupIds} onChange={(ids) => set('groupIds', ids)} placeholder="Buscar grupo" />
            {touched && errors.scope && <p className="text-[11px] font-semibold text-status-blocked">{errors.scope}</p>}
            {initial && (
              <p className="text-[10px] text-muted-foreground">
                O vínculo é aditivo; para remover um alcance, exclua e recrie a regra.
              </p>
            )}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Destinatários (usuários)</p>
            <MultiSelect options={users} selected={form.userIds} onChange={(ids) => set('userIds', ids)} placeholder="Buscar usuário" />
            <p className="text-[10px] text-muted-foreground">Além de você (criador). Vazio = só o criador. Aditivo.</p>
          </div>

          {deliveryGaps.length > 0 && (
            <Warn>
              <span className="font-bold">Canal ligado que não vai entregar.</span>{' '}
              {deliveryGaps.map((g) => describeGap(g)).join(' ')}
            </Warn>
          )}
          <Warn>
            <span className="font-bold">Enquanto a condição valer, ela substitui os outros alarmes.</span> O alarme
            personalizado ocupa o mesmo campo que o rastreador usa para reportar corte de energia e pânico.
            Prefira condições que representem um estado curto, e não algo que fique ligado o dia inteiro.
          </Warn>

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (window.confirm('Excluir a condição e a regra de envio deste alarme?')) deleteMutation.mutate();
              }}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Lê os atributos das últimas posições de um veículo e alimenta o `datalist` do campo de atributo.
 *
 * Existe porque o JEXL roda em modo estrito: citar um atributo que aquele veículo não manda faz a
 * expressão levantar exceção e nunca disparar, sem erro visível. Sugerir só o que o equipamento
 * realmente envia é o que evita esse erro na origem.
 */
function AttributePicker({ devices, onPickDevice }: { devices: Opt[]; onPickDevice: (id: number) => void }) {
  const [deviceId, setDeviceId] = useState<number | ''>('');
  const [rows, setRows] = useState<{ key: string; label: string; value: string }[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const read = async () => {
    if (!deviceId) return;
    setLoading(true);
    setStatus(null);
    try {
      const to = new Date();
      const win = async (hours: number) =>
        reportsApi.route({
          deviceId: Number(deviceId),
          from: new Date(to.getTime() - hours * 3600 * 1000).toISOString(),
          to: to.toISOString(),
        });
      let positions = await win(24);
      if (positions.length < 5) {
        const wider = await win(24 * 30);
        if (wider.length > positions.length) positions = wider;
      }
      const merged = new Map<string, unknown>();
      for (const p of positions.slice(-5)) for (const [k, v] of Object.entries(p.attributes ?? {})) merged.set(k, v);
      const built = Array.from(merged.entries())
        .map(([key, value]) => ({ key, label: translateAttribute(key), value: formatAttributeValue(key, value) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
      setRows(built);
      setStatus(built.length ? `${built.length} atributos lidos deste veículo.` : 'Sem atributos nas últimas posições.');
      onPickDevice(Number(deviceId));
    } catch (e) {
      setStatus(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-2">
      <p className="text-[11px] font-semibold text-muted-foreground">Descobrir atributos do veículo</p>
      <div className="flex gap-2">
        <select
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')}
          className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">— escolher veículo —</option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <Button type="button" variant="outline" size="sm" disabled={!deviceId || loading} onClick={() => void read()}>
          {loading ? 'Lendo…' : 'Ler posições'}
        </Button>
      </div>
      {status && <p className="text-[11px] text-muted-foreground">{status}</p>}
      {rows.length > 0 && (
        <>
          <datalist id="nautmax-attr-sugestoes">
            {rows.map((r) => (
              <option key={r.key} value={r.key}>{`${r.label} — atual: ${r.value}`}</option>
            ))}
          </datalist>
          <div className="max-h-28 overflow-y-auto rounded bg-secondary/40 p-1">
            {rows.map((r) => (
              <p key={r.key} className="flex gap-2 px-1 text-[11px]">
                <span className="font-mono font-semibold">{r.key}</span>
                <span className="truncate text-muted-foreground">{r.label} = {r.value}</span>
              </p>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-md bg-status-ready/10 px-3 py-2 text-[11px] text-status-ready">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 cursor-pointer rounded border-border accent-primary" />
      <span className="font-medium">{label}</span>
    </label>
  );
}

function MultiSelect({ options, selected, onChange, placeholder }: { options: Opt[]; selected: number[]; onChange: (ids: number[]) => void; placeholder: string }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.name.toLowerCase().includes(s)) : options;
  }, [options, q]);
  const toggle = (id: number) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="rounded-md border border-input">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-t-md border-b border-input bg-background px-2 text-sm outline-none"
      />
      <div className="max-h-32 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">Nada encontrado.</p>
        ) : (
          filtered.map((o) => (
            <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent">
              <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="h-4 w-4 accent-primary" />
              <span className="truncate">{o.name}</span>
            </label>
          ))
        )}
      </div>
      {selected.length > 0 && <p className="border-t border-input px-2 py-1 text-[11px] text-muted-foreground">{selected.length} selecionado(s)</p>}
    </div>
  );
}
